import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { Form } from '@rjsf/mui';
import { RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { AnyContentBlock, HeadingBlock, ParagraphBlock, ImageBlock, TableBlock } from '../models';
import { useContentBlocksSchema } from './ContentBlocksSchemaProvider';

interface ContentBlocksEditorProps {
  blocks: AnyContentBlock[];
  onChange: (blocks: AnyContentBlock[]) => void;
}

const ContentBlocksEditor: React.FC<ContentBlocksEditorProps> = ({ blocks, onChange }) => {
  const { schema, uiSchemas, loading, error } = useContentBlocksSchema();
  const [currentBlocks, setCurrentBlocks] = useState<AnyContentBlock[]>(blocks || []);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [currentEditIndex, setCurrentEditIndex] = useState<number | null>(null);
  const [selectedBlockType, setSelectedBlockType] = useState('');
  
  // Extract block types from the schema
  const blockTypes = useMemo(() => {
    if (!schema || !schema.items || typeof schema.items !== 'object') return [];
    
    // The schema is an array of content block types
    const items = schema.items as RJSFSchema;
    if (!items.anyOf || !Array.isArray(items.anyOf)) return [];
    
    return items.anyOf
      .filter((blockSchema): blockSchema is RJSFSchema => typeof blockSchema === 'object' && blockSchema !== null)
      .map((blockSchema) => {
        const type = blockSchema.title || '';
        return {
          value: type,
          label: type,
          schema: blockSchema,
          uiSchema: uiSchemas[type] || {}
        };
      });
  }, [schema, uiSchemas]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !schema) {
    return (
      <Box sx={{ p: 2, color: 'error.main' }}>
        Error loading content blocks schema. Please try again later.
      </Box>
    );
  }
  // Helper function to get display text for a block
  const getBlockDisplayText = (block: AnyContentBlock): string => {
    switch (block.type) {
      case 'Heading':
        return `H${(block as HeadingBlock).level}: ${(block as HeadingBlock).text}`;
      case 'Paragraph':
        const text = (block as ParagraphBlock).text;
        return text.length > 50 ? `${text.substring(0, 50)}...` : text;
      case 'Image':
        return `Image: ${(block as ImageBlock).caption || 'No caption'}`;
      case 'Table':
        return `Table: ${(block as TableBlock).headers.length} columns, ${(block as TableBlock).rows.length} rows`;
      case 'HTML':
        return 'HTML content';
      default:
        return 'Unknown block type';
    }
  };

  const handleAddBlockClick = () => {
    setAddDialogOpen(true);
  };

  const handleEditBlockClick = (index: number) => {
    setCurrentEditIndex(index);
    setSelectedBlockType(currentBlocks[index].type);
    setEditDialogOpen(true);
  };

  const handleDeleteBlock = (index: number) => {
    const updatedBlocks = [...currentBlocks];
    updatedBlocks.splice(index, 1);
    setCurrentBlocks(updatedBlocks);
    onChange(updatedBlocks);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const updatedBlocks = [...currentBlocks];
    const temp = updatedBlocks[index];
    updatedBlocks[index] = updatedBlocks[index - 1];
    updatedBlocks[index - 1] = temp;
    setCurrentBlocks(updatedBlocks);
    onChange(updatedBlocks);
  };

  const handleMoveDown = (index: number) => {
    if (index >= currentBlocks.length - 1) return;
    const updatedBlocks = [...currentBlocks];
    const temp = updatedBlocks[index];
    updatedBlocks[index] = updatedBlocks[index + 1];
    updatedBlocks[index + 1] = temp;
    setCurrentBlocks(updatedBlocks);
    onChange(updatedBlocks);
  };

  const handleBlockTypeSelect = (type: string) => {
    setSelectedBlockType(type);
  };

  const handleEditSubmit = ({ formData }: any) => {
    if (currentEditIndex === null) return;
    
    const updatedBlocks = [...currentBlocks];
    updatedBlocks[currentEditIndex] = { 
      type: selectedBlockType,
      ...formData 
    } as AnyContentBlock;
    
    setCurrentBlocks(updatedBlocks);
    onChange(updatedBlocks);
    setEditDialogOpen(false);
    setCurrentEditIndex(null);
  };

  const handleAddSubmit = ({ formData }: any) => {
    const newBlock = { 
      type: selectedBlockType,
      ...formData 
    } as AnyContentBlock;
    
    const updatedBlocks = [...currentBlocks, newBlock];
    setCurrentBlocks(updatedBlocks);
    onChange(updatedBlocks);
    setAddDialogOpen(false);
    setSelectedBlockType('');
  };

  const selectedBlockTypeInfo = blockTypes.find(type => type.value === selectedBlockType);

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Content Blocks</Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={handleAddBlockClick}
        >
          Add Block
        </Button>
      </Box>

      {currentBlocks.length === 0 ? (
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary">No content blocks added yet</Typography>
        </Paper>
      ) : (
        <Paper sx={{ p: 2 }}>
          {currentBlocks.map((block, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Paper 
                elevation={1} 
                sx={{ 
                  p: 2, 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  bgcolor: 'background.default',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <Box>
                  <Typography variant="subtitle1" color="primary">
                    {block.type}
                  </Typography>
                  <Typography variant="body2">
                    {getBlockDisplayText(block)}
                  </Typography>
                </Box>
                <Box>
                  <Tooltip title="Move Up">
                    <span>
                      <IconButton 
                        size="small" 
                        onClick={() => handleMoveUp(index)}
                        disabled={index === 0}
                      >
                        <ArrowUpwardIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move Down">
                    <span>
                      <IconButton 
                        size="small" 
                        onClick={() => handleMoveDown(index)}
                        disabled={index === currentBlocks.length - 1}
                      >
                        <ArrowDownwardIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => handleEditBlockClick(index)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => handleDeleteBlock(index)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>
              {index < currentBlocks.length - 1 && <Divider sx={{ my: 1 }} />}
            </Box>
          ))}
        </Paper>
      )}

      {/* Add Block Dialog */}
      <Dialog 
        open={addDialogOpen} 
        onClose={() => setAddDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Content Block</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1 }}>
            <Typography variant="subtitle1">Select Block Type</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              {blockTypes.map((type) => (
                <Button
                  key={type.value}
                  variant={selectedBlockType === type.value ? "contained" : "outlined"}
                  onClick={() => handleBlockTypeSelect(type.value)}
                  sx={{ mb: 1 }}
                >
                  {type.label}
                </Button>
              ))}
            </Box>
          </Box>
          
          {selectedBlockType && selectedBlockTypeInfo && (
            <Form
              schema={selectedBlockTypeInfo.schema}
              uiSchema={selectedBlockTypeInfo.uiSchema}
              validator={validator}
              onSubmit={handleAddSubmit}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="primary"
            type="submit"
            form="root"
            disabled={!selectedBlockType}
          >
            Add Block
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Block Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Content Block</DialogTitle>
        <DialogContent>
          {currentEditIndex !== null && selectedBlockTypeInfo && (
            <Form
              schema={selectedBlockTypeInfo.schema}
              uiSchema={selectedBlockTypeInfo.uiSchema}
              validator={validator}
              formData={currentBlocks[currentEditIndex]}
              onSubmit={handleEditSubmit}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="primary"
            type="submit"
            form="root"
          >
            Update Block
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContentBlocksEditor;
